export interface StorageLike {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
}

export interface BestRecords {
  bestPoint: number;
  bestMeters: number;
}

export interface BestRecordStore {
  load(): BestRecords;
  save(records: BestRecords): BestRecords;
}

export interface BestScoreStore {
  load(): number;
  save(score: number): number;
}

function validPoint(value: unknown): number {
  const number = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(number) ? -Math.abs(Math.trunc(number)) : 0;
}

function validMeters(value: unknown): number {
  const number = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(number) && number >= 0 ? number : 0;
}

function normalizeRecords(value: Partial<BestRecords> | null | undefined): BestRecords {
  return {
    bestPoint: validPoint(value?.bestPoint),
    bestMeters: validMeters(value?.bestMeters),
  };
}

export function createBestRecordStore(
  storage: StorageLike,
  recordsKey = 'reverse-animal-tower:records',
  legacyPointKey = 'reverse-animal-tower:best',
): BestRecordStore {
  let memoryValue: BestRecords = { bestPoint: 0, bestMeters: 0 };

  const persist = () => {
    try {
      storage.setItem(recordsKey, JSON.stringify(memoryValue));
    } catch {
      // Browser policy failures never block play; memory remains authoritative.
    }
  };

  return {
    load() {
      try {
        const serialized = storage.getItem(recordsKey);
        if (serialized !== null) {
          try {
            const parsed = JSON.parse(serialized) as Partial<BestRecords> | null;
            if (parsed && typeof parsed === 'object') {
              const normalized = normalizeRecords(parsed);
              const needsMigration = parsed.bestPoint !== normalized.bestPoint
                || parsed.bestMeters !== normalized.bestMeters;
              memoryValue = normalized;
              if (needsMigration) persist();
              return { ...memoryValue };
            }
          } catch {
            // Invalid new data falls through to safe legacy migration.
          }
        }

        memoryValue = {
          bestPoint: validPoint(storage.getItem(legacyPointKey)),
          bestMeters: 0,
        };
        persist();
      } catch {
        // The memory fallback remains authoritative for this session.
      }
      return { ...memoryValue };
    },
    save(records) {
      memoryValue = normalizeRecords(records);
      persist();
      return { ...memoryValue };
    },
  };
}

/** @deprecated Compatibility adapter while callers migrate to dual records. */
export function createBestScoreStore(storage: StorageLike, key = 'reverse-animal-tower:best'): BestScoreStore {
  const records = createBestRecordStore(storage, `${key}:records`, key);
  return {
    load: () => records.load().bestPoint,
    save: (score) => records.save({ bestPoint: score, bestMeters: 0 }).bestPoint,
  };
}
